import { ICotizacionValues } from '../../../Providers/CotizacionStore';
import { IEmisionValues } from '../../../Providers/EmisionStore';
import { BffError } from './bffError';
import { mensajeDeError } from './errorMessages';
import { soloTextos } from './avisosEmbudo';
import { autoViaBffDesdeConfig, isAutoViaBff } from './flag';
import { aSexo, interpretarAnio } from './funnel';
import { loadConfig } from '../../../../utils/configLoader';

export { advertenciasDe } from './avisosEmbudo';
export type { EmbudoAdvertencia } from './avisosEmbudo';

export { MENSAJE_RECHAZO_POR_DEFECTO } from './errorMessages';

/**
 * Embudo web EOL (US 23704, contrato del BFF en la US 23697 / wiki 26).
 *
 * Con el embudo prendido en el BFF, `POST pasos` bindea `datos` al record del
 * paso de la wiki: lo que la wiki no declara no cruza. Por eso los pasos
 * `titular`, `radicacion`, `vehiculo` y `pago` salen con las claves de la wiki,
 * no con las del monolito.
 *
 * Flag runtime `embudo_eol` (react_config.json), apagado por default. Apagado,
 * el front manda los pasos exactamente como antes.
 */

/** Pasos de la wiki 26 que informa el front por `POST pasos`. */
export type PasoEmbudo = 'titular' | 'radicacion' | 'vehiculo' | 'pago';

export interface TitularWiki {
  numeroDocumento?: string;
  tipoDocumento?: string;
  /** ISO yyyy-MM-dd. */
  fechaNacimiento?: string;
  genero?: string;
}

/** Nunca el numero completo ni el codigo de seguridad (wiki 26, decision del 21/10). */
export interface PagoWiki {
  medioPago?: string;
  marca?: string;
  ultimos4?: string;
  /** MM/AA. */
  vencimiento?: string;
  titular?: string;
  banco?: string;
}

export interface RadicacionWiki {
  calle?: string;
  altura?: string;
  piso?: string;
  departamento?: string;
  localidad?: string;
  provincia?: string;
}

export interface VehiculoWiki {
  patente?: string;
  marca?: string;
  modelo?: string;
  version?: string;
  anio?: number;
  uso?: string;
  tieneGnc?: boolean;
  esCeroKm?: boolean;
  chasis?: string;
  nroMotor?: string;
}

/**
 * Sincronico, para componentes con el config en mano. Exige el camino BFF:
 * sin `auto_via_bff` no hay embudo.
 */
export const embudoEolDesdeConfig = (
  config?: { auto_via_bff?: boolean; embudo_eol?: boolean; pais?: string } | null
): boolean => autoViaBffDesdeConfig(config) && config?.embudo_eol === true;

/** Misma semantica que `embudoEolDesdeConfig`, leyendo el config runtime. */
export const isEmbudoEol = async (): Promise<boolean> => {
  if (!(await isAutoViaBff())) return false;
  try {
    const config = await loadConfig();
    return config?.embudo_eol === true;
  } catch {
    return false;
  }
};

const texto = (valor?: string | null): string | undefined => {
  const limpio = (valor ?? '').trim();
  return limpio ? limpio : undefined;
};

/** Saca las claves `undefined`: el BFF no las necesita y el JSON queda igual al de la wiki. */
const sinVacios = <T extends object>(objeto: T): T => {
  const salida: Record<string, unknown> = {};
  Object.entries(objeto).forEach(([clave, valor]) => {
    if (valor !== undefined) salida[clave] = valor;
  });
  return salida as T;
};

/** 45 · `radicacion`. Obligatorio para Salesforce: calle. */
export const datosRadicacion = (
  emision: IEmisionValues,
  cotizacion: ICotizacionValues
): RadicacionWiki =>
  sinVacios({
    calle: texto(emision.calle),
    altura: texto(emision.altura),
    piso: texto(emision.piso),
    departamento: texto(emision.departamento),
    localidad: texto(emision.localidad),
    provincia: texto(cotizacion.provincia?.value),
  });

/** "A/D" es la patente "a designar" del formulario: no es una patente. */
const PATENTE_A_DESIGNAR = 'A/D';

/**
 * 50 · `vehiculo`. Obligatorios para Salesforce: marca, modelo y anio (numero).
 * Salen de la cotizacion: la pantalla del vehiculo no los vuelve a pedir.
 */
export const datosVehiculo = (
  emision: IEmisionValues,
  cotizacion: ICotizacionValues
): VehiculoWiki => {
  const { anio, esCeroKm } = interpretarAnio(cotizacion.año?.value);
  const patente = texto(emision.patente);
  return sinVacios({
    patente: patente && patente !== PATENTE_A_DESIGNAR ? patente.toUpperCase() : undefined,
    marca: texto(cotizacion.marca?.value),
    modelo: texto(cotizacion.modelo?.value),
    version: texto(cotizacion.version?.value),
    anio: anio || undefined,
    uso: texto(emision.tipoUso?.label),
    tieneGnc: typeof emision.gnc === 'boolean' ? emision.gnc : undefined,
    esCeroKm,
    chasis: emision.numChasis ?? undefined,
    nroMotor: emision.numMotor ?? undefined,
  });
};

/** El cotizador de auto/moto solo pide DNI (el CUIL se calcula a partir de el). */
const TIPO_DOCUMENTO = 'DNI';

/** 40 · `titular`. Obligatorios para Salesforce: numeroDocumento y tipoDocumento. */
export const datosTitular = (
  emision: IEmisionValues,
  cotizacion: ICotizacionValues
): TitularWiki => {
  const { dia, mes, anio } = emision.fechaNac || ({} as Partial<NonNullable<IEmisionValues['fechaNac']>>);
  const fechaNacimiento =
    dia && mes && anio ? `${anio}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}` : undefined;
  return sinVacios({
    numeroDocumento: texto(emision.numDoc),
    tipoDocumento: TIPO_DOCUMENTO,
    fechaNacimiento,
    genero: cotizacion.genero?.value ? aSexo(cotizacion.genero.value) : undefined,
  });
};

/** La emision online solo acepta tarjeta de credito (`payment_method_not_supported`). */
const MEDIO_PAGO = 'TARJETA DE CREDITO';

/**
 * 60 · `pago`. Solo lo que la wiki permite: el numero completo y el codigo de
 * seguridad no salen nunca de esta funcion.
 */
export const datosPago = (emision: IEmisionValues): PagoWiki => {
  const digitos = (emision.numeroTarjeta || '').replace(/\D/g, '');
  const { mes, anio } = emision.vencimientoTarjeta || ({} as Partial<NonNullable<IEmisionValues['vencimientoTarjeta']>>);
  return sinVacios({
    medioPago: MEDIO_PAGO,
    marca: texto(emision.tipoTarjeta?.label),
    ultimos4: digitos.length >= 13 ? digitos.slice(-4) : undefined,
    vencimiento: mes && anio ? `${mes.padStart(2, '0')}/${anio.slice(-2)}` : undefined,
    titular: texto(emision.titularTarjeta),
    banco: texto(emision.banco?.label),
  });
};

/* ───────────────── 422: el embudo frena el paso ───────────────── */

/** Rechazo de Salesforce: frena al cliente, marca los campos y no se reintenta. */
export interface EmbudoRechazo {
  codigo: string;
  /** Mensaje de Salesforce: es para el cliente (dice que dato corregir). */
  mensaje: string;
  /** Claves de la wiki 26 (`numeroDocumento`, `patente`, ...). */
  campos: string[];
}

/**
 * Rechazo del embudo si el error es un 422 `embudo_rechazado`; null para
 * cualquier otro (un 500, un timeout o el bridge caido no frenan: el BFF
 * reintenta el mismo paso).
 */
export const rechazoDelEmbudo = (error: BffError): EmbudoRechazo | null => {
  if (error.code !== 'embudo_rechazado') return null;
  const { codigo, campos } = error.details || {};
  return {
    codigo: typeof codigo === 'string' ? codigo : '',
    mensaje: mensajeDeError(error),
    campos: soloTextos(campos),
  };
};

/**
 * Campo de la wiki -> input de la pantalla de cada paso. Lo que no esta aca no
 * se puede marcar (ej. marca y modelo vienen de la cotizacion): se muestra solo
 * el mensaje. `pago` no marca inputs: su pantalla muestra el mensaje junto al boton.
 */
const CAMPOS_FORMULARIO: Record<PasoEmbudo, Record<string, string>> = {
  titular: { numeroDocumento: 'numDoc' },
  radicacion: {
    calle: 'calle',
    altura: 'altura',
    piso: 'piso',
    departamento: 'departamento',
  },
  vehiculo: { patente: 'patente', chasis: 'numChasis', nroMotor: 'numMotor' },
  pago: {},
};

/** Inputs de la pantalla del `paso` que hay que marcar para los `campos` del 422. */
export const camposDelFormulario = (paso: PasoEmbudo, campos: string[]): string[] =>
  campos
    .map((campo) => CAMPOS_FORMULARIO[paso][campo])
    .filter((campo): campo is string => !!campo);
